<script lang="ts">
  import {LoaderPlus} from '@/components';
  import {draftStore} from '@/store';
  import {ProcessState} from '@/types/common';
  import {onDestroy, onMount} from 'svelte';
  import {writable} from 'svelte/store';
  import Swal from 'sweetalert2';

  export let draftId: any;

  let descriptions: Array<string> = [];
  let isGenerating: boolean = false;
  let currentPage = writable(0);
  $: totalPages = Array.isArray(descriptions) && descriptions.length != 0 ? descriptions.length : 0;

  function goToPage(index: number) {
    if (index >= 0 && index < totalPages) {
      currentPage.set(index);
    }
  }

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(descriptions[$currentPage]);
      Swal.fire({
        position: 'bottom-end',
        icon: 'success',
        title: 'Text Copied to Clipboard',
        showConfirmButton: false,
        timer: 1500,
        toast: true
      });
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  }

  function downloadDescriptions() {
    const blob = new Blob([descriptions.join('\n')], {type: 'text/plain'});
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'social-descriptions.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    Swal.fire({
      position: 'bottom-end',
      icon: 'success',
      title: 'Descriptions Downloaded',
      showConfirmButton: false,
      timer: 1500,
      toast: true
    });
  }

  const descriptionSubscription = draftStore.subscribe(draft => {
    descriptions = draft.descriptions as Array<string>;
    isGenerating = false;
  });

  onMount(async () => {
    if (totalPages == 0 || draftId != undefined) {
      isGenerating = true;
      descriptions = await draftStore.generateSocialDescriptions(draftId);
      isGenerating = false;
    }
  });

  onDestroy(() => {
    descriptionSubscription();
  });
</script>

{#if totalPages != 0 || isGenerating}
  <div class="w-full space-y-4 pt-6">
    <hr />
    <!-- Header -->
    <div class="flex content-center text-center align-middle">
      <span class="text-md w-full font-medium"> Social Descriptions </span>
    </div>
    {#if isGenerating}
      <div class="mx-auto flex max-w-md flex-col items-center p-6">
        <LoaderPlus status={ProcessState.BUSY} heading="Retrieving Social Descriptions" />
      </div>
    {:else}
      <!-- Content -->
      <div class="mx-auto flex max-w-md flex-col items-center p-6">
        <p class="text-md mb-4 text-center leading-loose text-zinc-800">{descriptions[$currentPage]}</p>
      </div>
      <!-- Footer -->
      <div class="flex w-full justify-between">
        <!-- Pagination -->
        <div class="flex content-center justify-start gap-2">
          <button
            class="mr-2 rounded-full border px-4 py-1 shadow-lg disabled:opacity-50"
            on:click={() => goToPage($currentPage - 1)}
            disabled={$currentPage === 0}
          >
            <i class="fa-solid fa-angle-left"></i>
          </button>

          {#each Array(totalPages) as _, index}
            <button
              class="rounded-md border px-4 py-1 {$currentPage === index
                ? 'bg-primary font-bold text-white'
                : 'bg-white text-zinc-500'}"
              on:click={() => goToPage(index)}
            >
              {index + 1}
            </button>
          {/each}

          <button
            class="ml-2 rounded-full border px-4 py-1 shadow-lg disabled:opacity-50"
            on:click={() => goToPage($currentPage + 1)}
            disabled={$currentPage === totalPages - 1}
          >
            <i class="fa-solid fa-angle-right"></i>
          </button>
        </div>
        <!-- Action Buttons -->
        <div class="flex content-center justify-end space-x-4">
          <button
            on:click={() => copyToClipboard()}
            class="flex w-full items-center rounded-lg border border-2 border-zinc-400 bg-white px-4 px-4 py-2 py-2 font-medium text-zinc-400 hover:bg-zinc-400 hover:text-white lg:w-auto"
          >
            Copy Text
          </button>
          <button
            on:click={() => downloadDescriptions()}
            class="flex w-full items-center rounded-lg border border-2 border-rose-500 bg-white px-4 px-4 py-2 py-2 font-medium text-rose-500 hover:bg-primary hover:text-white lg:w-auto"
          >
            Download Descriptions
          </button>
        </div>
      </div>
    {/if}
  </div>
{/if}
